import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { adminApi, type AdminUserItem } from '@/services/api';

export default function AdminUsersPage() {
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<AdminUserItem | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminApi
      .listUsers({ search, role: roleFilter, status: statusFilter, page: 1, pageSize: 100 })
      .then((res) => {
        setUsers(res.list);
        setTotal(res.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleStatus = (u: AdminUserItem) => {
    const next = u.status === 'active' ? 'disabled' : 'active';
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, status: next } : x)));
    adminApi.setUserStatus(u.id, next).catch(() => fetchData());
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">用户管理</h2>
            <p className="text-sm text-foreground-500 mt-0.5">共 {total} 个注册用户</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索企业名或手机号..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-400"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-700 focus:outline-none focus:border-foreground-400 cursor-pointer"
          >
            <option value="">全部角色</option>
            <option value="supplier">供应方</option>
            <option value="buyer">需求方</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-700 focus:outline-none focus:border-foreground-400 cursor-pointer"
          >
            <option value="">全部状态</option>
            <option value="active">正常</option>
            <option value="disabled">已禁用</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">企业名称</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">角色</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">联系人</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden md:table-cell">电话</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden lg:table-cell">地区</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap hidden lg:table-cell">挂牌数</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">状态</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-10 text-foreground-400">加载中...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-10 text-foreground-400">暂无用户</td></tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-sm font-medium text-foreground-900 hover:text-foreground-700 transition-colors cursor-pointer text-left"
                        >
                          {user.companyName || user.contactName}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'supplier' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                        }`}>
                          {user.role === 'supplier' ? '供应方' : '需求方'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{user.contactName}</td>
                      <td className="px-4 py-3 text-foreground-500 hidden md:table-cell">{user.phone}</td>
                      <td className="px-4 py-3 text-foreground-500 hidden lg:table-cell">{user.region}</td>
                      <td className="px-4 py-3 text-foreground-700 hidden lg:table-cell">{user.listingCount}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.status === 'active' ? '正常' : '已禁用'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="px-2.5 py-1 rounded-md text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            详情
                          </button>
                          <button
                            onClick={() => toggleStatus(user)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              user.status === 'active'
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                          >
                            {user.status === 'active' ? '禁用' : '启用'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail modal */}
        {selectedUser && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/40" onClick={() => setSelectedUser(null)} />
            <div className="relative bg-white rounded-xl p-5 w-full max-w-sm border border-background-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground-950">用户详情</h3>
                <button onClick={() => setSelectedUser(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-foreground-500">企业名称</span><span className="text-foreground-900 font-medium">{selectedUser.companyName || '--'}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">角色</span><span className="text-foreground-900">{selectedUser.role === 'supplier' ? '供应方' : '需求方'}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">联系人</span><span className="text-foreground-900">{selectedUser.contactName}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">电话</span><span className="text-foreground-900">{selectedUser.phone}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">地区</span><span className="text-foreground-900">{selectedUser.region || '--'}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">挂牌数</span><span className="text-foreground-900">{selectedUser.listingCount}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">注册时间</span><span className="text-foreground-900">{new Date(selectedUser.createdAt).toLocaleDateString('zh-CN')}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">状态</span><span className={selectedUser.status === 'active' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>{selectedUser.status === 'active' ? '正常' : '已禁用'}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
